import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface CarLoaderProps {
  className?: string
  text?: string
}

// Frames of the "loading" marker in car-loader.json (car waiting at the red light).
const LOADING_SEGMENT: [number, number] = [0, 90]
const STILL_FRAME = 45

/**
 * Full-page / route loading state: a car waiting at a red light.
 * lottie-web (light build) and the animation JSON are loaded on demand, so neither is in the entry bundle.
 * With prefers-reduced-motion the animation is parked on one still frame instead of looping.
 * Use <Skeleton> / <TableSkeleton> for tables and cards, where the final layout is known.
 */
export function CarLoader({ className, text = 'Loading' }: CarLoaderProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    let cancelled = false
    let destroy: (() => void) | undefined

    Promise.all([import('lottie-web/build/player/lottie_light'), import('@/assets/lottie/car-loader.json')]).then(
      ([{ default: lottie }, { default: animationData }]) => {
        if (cancelled) return
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const animation = lottie.loadAnimation({
          container,
          renderer: 'svg',
          loop: !reduceMotion,
          autoplay: false,
          animationData,
          rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
        })
        if (reduceMotion) animation.goToAndStop(STILL_FRAME, true)
        else animation.playSegments(LOADING_SEGMENT, true)
        destroy = () => animation.destroy()
      },
      () => {
        // Animation is decorative; the text label below still reports the loading state.
      },
    )

    return () => {
      cancelled = true
      destroy?.()
    }
  }, [])

  return (
    <div role="status" className={cn('flex flex-col items-center justify-center gap-2', className)}>
      <div ref={containerRef} aria-hidden="true" className="aspect-[2/1] w-60 max-w-full" />
      <p className="text-base text-text-muted">{text}</p>
    </div>
  )
}
